/**
 * Module dependencies.
 */

import React from 'react';
import ReactJson from 'react-json-view';
import { Tooltip } from 'antd';
import qs from 'qs';

/**
 * `LogBodyRendererProps` type.
 */

type LogBodyRendererProps = {
  body: any;
  type: string;
};

/**
 * Parse body if it's a string.
 */

function parseBody(body: any): any {
  try {
    return typeof body === 'string' ? JSON.parse(body) : body;
  } catch (error) {
    return null;
  }
}

/**
 * Method colors for HTTP methods.
 */

const methodColors: Record<string, string> = {
  GET: '#52c41a',
  POST: '#1890ff',
  PUT: '#faad14',
  PATCH: '#13c2c2',
  DELETE: '#f5222d'
};

/**
 * Render URL with query params badge if present.
 */

function renderUrl(url: string): JSX.Element {
  const urlParts = url.split('?');
  const baseUrl = urlParts[0];
  const queryString = urlParts[1];

  if (!queryString) {
    return <span style={{ fontSize: '13px', color: '#000000', fontFamily: 'monospace' }}>{url}</span>;
  }

  const params = qs.parse(queryString);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ fontSize: '13px', color: '#000000', fontFamily: 'monospace' }}>{baseUrl}</span>
      <Tooltip
        overlayStyle={{ maxWidth: '600px' }}
        title={<pre style={{ margin: 0, fontSize: '12px' }}>{JSON.stringify(params, null, 2)}</pre>}
      >
        <span
          style={{
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: '#1890ff',
            cursor: 'pointer'
          }}
        >
          {'params'}
        </span>
      </Tooltip>
    </div>
  );
}

/**
 * Render task type - show messages with context badge if present.
 */

function renderTask(parsedBody: any) {
  const messages = parsedBody?.messages;
  const context = parsedBody?.context;

  if (!messages || !Array.isArray(messages)) {
    return renderDefault(parsedBody);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Show context badge and URL if exists */}
      {context && context.method && context.statusCode !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: methodColors[context.method] || '#d9d9d9'
            }}
          >
            {context.method}
          </span>
          <span
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: context.statusCode >= 400 ? '#f5222d' : '#1890ff'
            }}
          >
            {context.statusCode}
          </span>
          {context.url && renderUrl(context.url)}
        </div>
      )}

      {/* Messages */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {messages.map((message: any, index: number) => {
          // If message is a string, show it directly
          if (typeof message === 'string') {
            return (
              <span key={index} style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6' }}>
                {message}
              </span>
            );
          }

          // If message is an object, show badge with tooltip
          return (
            <Tooltip
              key={index}
              overlayStyle={{ maxWidth: '600px' }}
              title={<pre style={{ margin: 0, fontSize: '12px' }}>{JSON.stringify(message, null, 2)}</pre>}
            >
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#722ed1',
                  cursor: 'pointer'
                }}
              >
                {'Object'}
              </span>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Render verbose type - show messages array with badge for objects.
 */

function renderVerbose(parsedBody: any) {
  const messages = parsedBody?.messages;

  if (!messages || !Array.isArray(messages)) {
    return renderDefault(parsedBody);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      {messages.map((message: any, index: number) => {
        // If message is a string, show it directly
        if (typeof message === 'string') {
          return (
            <span key={index} style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6' }}>
              {message}
            </span>
          );
        }

        // If message is an object, show badge with tooltip
        return (
          <Tooltip
            key={index}
            overlayStyle={{ maxWidth: '600px' }}
            title={<pre style={{ margin: 0, fontSize: '12px' }}>{JSON.stringify(message, null, 2)}</pre>}
          >
            <span
              style={{
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#fff',
                backgroundColor: '#722ed1',
                cursor: 'pointer'
              }}
            >
              {'Object'}
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
}

/**
 * Render info type - show messages array with badge for objects.
 */

function renderInfo(parsedBody: any) {
  const messages = parsedBody?.messages;

  // If messages is an array, render each item
  if (Array.isArray(messages)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {messages.map((item: any, index: number) => {
          // If item is a string, show it directly
          if (typeof item === 'string') {
            return (
              <span key={index} style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6' }}>
                {item}
              </span>
            );
          }

          // If item is an object, show badge with tooltip
          return (
            <Tooltip
              key={index}
              overlayStyle={{ maxWidth: '600px' }}
              title={<pre style={{ margin: 0, fontSize: '12px' }}>{JSON.stringify(item, null, 2)}</pre>}
            >
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#722ed1',
                  cursor: 'pointer'
                }}
              >
                {'Object'}
              </span>
            </Tooltip>
          );
        })}
      </div>
    );
  }

  // Fallback: check for single message property
  const message = parsedBody?.message;

  if (!message) {
    return renderDefault(parsedBody);
  }

  // If message is a string, show it directly
  if (typeof message === 'string') {
    return <div style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6' }}>{message}</div>;
  }

  // If message is an object, show badge with tooltip
  return (
    <Tooltip
      overlayStyle={{ maxWidth: '600px' }}
      title={<pre style={{ margin: 0, fontSize: '12px' }}>{JSON.stringify(message, null, 2)}</pre>}
    >
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#fff',
          backgroundColor: '#722ed1',
          cursor: 'pointer'
        }}
      >
        {'Object'}
      </span>
    </Tooltip>
  );
}

/**
 * Render request type - show method badge and URL.
 */

function renderRequest(parsedBody: any) {
  const context = parsedBody?.context;

  if (!context || !context.url || !context.method) {
    return renderDefault(parsedBody);
  }

  const methodColors: Record<string, string> = {
    GET: '#52c41a',
    POST: '#1890ff',
    PUT: '#faad14',
    PATCH: '#13c2c2',
    DELETE: '#f5222d'
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#fff',
          backgroundColor: methodColors[context.method] || '#d9d9d9'
        }}
      >
        {context.method}
      </span>
      {renderUrl(context.url)}
    </div>
  );
}

/**
 * Render response type - show method, status code and URL.
 */

function renderResponse(parsedBody: any) {
  const context = parsedBody?.context;

  if (!context || !context.url || !context.method || context.statusCode === undefined) {
    return renderDefault(parsedBody);
  }

  const methodColors: Record<string, string> = {
    GET: '#52c41a',
    POST: '#1890ff',
    PUT: '#faad14',
    PATCH: '#13c2c2',
    DELETE: '#f5222d'
  };

  // Status code colors based on HTTP status ranges
  const getStatusColor = (statusCode: number): string => {
    if (statusCode >= 200 && statusCode < 300) return '#52c41a'; // Success - green
    if (statusCode >= 300 && statusCode < 400) return '#1890ff'; // Redirect - blue
    if (statusCode >= 400 && statusCode < 500) return '#faad14'; // Client error - orange
    if (statusCode >= 500) return '#f5222d'; // Server error - red
    return '#d9d9d9'; // Unknown - gray
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#fff',
          backgroundColor: methodColors[context.method] || '#d9d9d9'
        }}
      >
        {context.method}
      </span>
      <span
        style={{
          padding: '2px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#fff',
          backgroundColor: getStatusColor(context.statusCode)
        }}
      >
        {context.statusCode}
      </span>
      {renderUrl(context.url)}
    </div>
  );
}

/**
 * Render error type - show context (method + URL) and first message.
 */

function renderError(parsedBody: any) {
  const context = parsedBody?.context;
  const messages = parsedBody?.messages;
  const firstMessage = messages && messages.length > 0 ? messages[0] : null;

  const methodColors: Record<string, string> = {
    GET: '#52c41a',
    POST: '#1890ff',
    PUT: '#faad14',
    PATCH: '#13c2c2',
    DELETE: '#f5222d'
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap' }}>
      {/* Context: Method + URL */}
      {context && context.method && context.url && (
        <>
          <span
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: methodColors[context.method] || '#d9d9d9',
              flexShrink: 0
            }}
          >
            {context.method}
          </span>
          {renderUrl(context.url)}
        </>
      )}

      {/* First message */}
      {firstMessage && (
        <>
          {typeof firstMessage === 'string' ? (
            // String message
            <span style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6' }}>{firstMessage}</span>
          ) : (
            // Object message: show errorType: message + ERROR badge with tooltip
            <>
              <span style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6' }}>
                {firstMessage.errorType ? `${firstMessage.errorType}: ` : ''}
                {firstMessage.message || ''}
              </span>
              <Tooltip
                overlayStyle={{ maxWidth: '600px' }}
                title={<pre style={{ margin: 0, fontSize: '12px' }}>{JSON.stringify(firstMessage, null, 2)}</pre>}
              >
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: '#f5222d',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                >
                  {'ERROR'}
                </span>
              </Tooltip>
            </>
          )}
        </>
      )}

      {/* If no messages, show default JSON */}
      {!firstMessage && !context && renderDefault(parsedBody)}
    </div>
  );
}

/**
 * Render default - show JSON viewer.
 */

function renderDefault(parsedBody: any) {
  return (
    <ReactJson
      collapsed={1}
      displayDataTypes={false}
      displayObjectSize={false}
      enableClipboard={true}
      name={false}
      src={parsedBody}
      theme={'monokai'}
    />
  );
}

/**
 * Export `LogBodyRenderer` component.
 */

export function LogBodyRenderer({ body, type }: LogBodyRendererProps): JSX.Element {
  const parsedBody = parseBody(body);

  if (!parsedBody) {
    return <span style={{ color: '#999' }}>{'Invalid JSON'}</span>;
  }

  // Route to specific renderer based on type
  switch (type.toLowerCase()) {
    case 'task':
      return <>{renderTask(parsedBody)}</>;

    case 'verbose':
      return <>{renderVerbose(parsedBody)}</>;

    case 'info':
      return <>{renderInfo(parsedBody)}</>;

    case 'request':
      return <>{renderRequest(parsedBody)}</>;

    case 'response':
      return <>{renderResponse(parsedBody)}</>;

    case 'error':
      return <>{renderError(parsedBody)}</>;

    default:
      return <>{renderDefault(parsedBody)}</>;
  }
}
